import React, { useState } from "react";
import { Lead, Section, P, Callout, CTA } from "../../../../blog/components";

export const title = "跑分是假的";
export const excerpt =
  "一支伯克利团队在八个顶级 Agent benchmark 中的六个上刷到约 100%，却没解开任何一道题。在其中一个上，字符串 {} 通过了全部 890 道。那个数字从来就没在衡量 Agent。";
export const heroAlt = "莫比乌斯风格的风景：一轮红日从矮云背后升起，越过一片辽阔苍白的平原，从一处岩石峭壁边缘望去，崖沿上站着两个小小的人影";
export const readingTime = "5 分钟阅读";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5];

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/* ── Sketched visuals ─────────────────────────────────────────────────────────
   Same hand-drawn (Excalidraw-ish) look as the rest of the blog: a handwriting
   face, wobbly borders via asymmetric border-radii. Labels stay un-roughened so
   they read clearly, and are never set in all-caps.
   ───────────────────────────────────────────────────────────────────────────── */

/** Wraps a graphic in a hand-drawn card with an optional handwritten caption. */
function Figure({ children, caption, i = 0 }: { children: React.ReactNode; caption?: string; i?: number }) {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        style={{
          padding: "22px 18px",
          borderRadius: wonk(i),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        {children}
      </div>
      {caption && (
        <figcaption
          style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Interactive mock eval harness. Pick a lazy answer, watch the score sit at
 * 100%, then flip "actually read the answer" on and watch it collapse to zero.
 * This is the FieldWorkArena bug in miniature: the validator only checked that
 * the last message came from the assistant, never what the message said.
 */
function EvalHarness() {
  const ANSWERS = [
    { id: "obj", label: "{}", note: "一个空对象" },
    { id: "done", label: '"I’m done"', note: "没附带任何工作" },
    { id: "blank", label: "（空消息）", note: "什么都没有" },
  ];
  const [picked, setPicked] = useState("obj");
  const [reads, setReads] = useState(false);
  const score = reads ? 0 : 100;
  const colour = reads ? FAIL : BLUE;
  const current = ANSWERS.find((a) => a.id === picked) ?? ANSWERS[0]!;

  return (
    <div style={{ fontFamily: HAND }}>
      <div style={{ fontSize: 16, color: "var(--text-secondary)", marginBottom: 8 }}>
        给全部 890 道题提交一个答案
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        {ANSWERS.map((a, i) => {
          const on = a.id === picked;
          return (
            <button
              key={a.id}
              onClick={() => setPicked(a.id)}
              style={{
                cursor: "pointer",
                padding: "8px 14px",
                fontFamily: HAND,
                fontSize: 16,
                borderRadius: wonk(i),
                color: on ? "#fff" : "var(--text-primary)",
                background: on ? BLUE : "var(--surface-5)",
                border: `1.8px solid ${on ? BLUE : "var(--border-strong)"}`,
                transform: `rotate(${tilt(i) * 0.6}deg)`,
              }}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 16,
          padding: "16px 18px",
          borderRadius: wonk(2),
          border: "2px solid var(--border-strong)",
          background: "var(--surface-5)",
        }}
      >
        <div>
          <div style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 6 }}>验证器运行的是：</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            pass = last_message.role == "assistant"
            {reads && (
              <>
                <br />
                <span style={{ color: FAIL }}>&amp;&amp; answer_is_correct(last_message)</span>
              </>
            )}
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 8 }}>
            你发出了 {current.label}，{current.note}
          </div>
        </div>
        <div style={{ textAlign: "center", minWidth: 96 }}>
          <div style={{ fontSize: 52, lineHeight: 1, color: colour, fontWeight: 700 }}>{score}%</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
            {reads ? "解开的题：0" : "通过率"}
          </div>
        </div>
      </div>

      <button
        onClick={() => setReads((r) => !r)}
        style={{
          cursor: "pointer",
          marginTop: 14,
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          padding: "11px 16px",
          fontFamily: HAND,
          fontSize: 16,
          textAlign: "left",
          color: "var(--text-primary)",
          borderRadius: wonk(3),
          background: "var(--surface-4)",
          border: `2px solid ${reads ? FAIL : "var(--border-strong)"}`,
        }}
      >
        <span
          aria-hidden
          style={{
            position: "relative",
            width: 46,
            height: 26,
            borderRadius: 999,
            background: reads ? FAIL : "var(--border-strong)",
            transition: "background 120ms",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: reads ? 23 : 3,
              width: 20,
              height: 20,
              borderRadius: 999,
              background: "#fff",
              transition: "left 120ms",
            }}
          />
        </span>
        真的去读一读答案：{reads ? "开" : "关"}
      </button>
    </div>
  );
}

/** Sketched bar chart: the eight benchmarks an exploit agent gamed, and the score it got on each. */
function ScoreBars() {
  const rows = [
    { name: "Terminal-Bench", tasks: "89 道题", score: 100 },
    { name: "SWE-bench Verified", tasks: "500 道题", score: 100 },
    { name: "SWE-bench Pro", tasks: "731 道题", score: 100 },
    { name: "FieldWorkArena", tasks: "890 道题", score: 100 },
    { name: "CAR-bench", tasks: "全部题目", score: 100 },
    { name: "WebArena", tasks: "812 道题", score: 100 },
    { name: "GAIA", tasks: "165 道题", score: 98 },
    { name: "OSWorld", tasks: "369 道题", score: 73 },
  ];
  return (
    <div style={{ fontFamily: HAND, display: "flex", flexDirection: "column", gap: 9 }}>
      {rows.map((r, i) => (
        <div key={r.name} style={{ display: "grid", gridTemplateColumns: "150px 1fr 44px", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 15, color: "var(--text-primary)", lineHeight: 1.15 }}>
            {r.name}
            <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{r.tasks}</span>
          </div>
          <div style={{ height: 18, borderRadius: 999, background: "var(--surface-5)", border: "1.5px solid var(--border-strong)", overflow: "hidden" }}>
            <div
              style={{
                width: `${r.score}%`,
                height: "100%",
                borderRadius: 999,
                background: BLUE,
                opacity: 0.55,
                transform: `rotate(${tilt(i) * 0.15}deg)`,
              }}
            />
          </div>
          <div style={{ fontSize: 16, color: BLUE, textAlign: "right" }}>{r.score}%</div>
        </div>
      ))}
    </div>
  );
}

/* ── References ───────────────────────────────────────────────────────────── */

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "H. Wang, Q. Mang, A. Cheung, K. Sen, D. Song，《How We Broke Top AI Agent Benchmarks》，UC Berkeley Center for Responsible Decentralized Intelligence（2026 年 4 月）。用于佐证：exploit agent 在八个 benchmark 中的六个上刷到 100% 而零 LLM 调用、FieldWorkArena 上横扫全部 890 道题的空对象漏洞、SWE-bench 的 conftest.py 钩子、WebArena 的 file:// 读取，以及各个 benchmark 的分数。",
    href: "https://rdi.berkeley.edu/blog/trustworthy-benchmarks-cont/",
    label: "rdi.berkeley.edu/blog/trustworthy-benchmarks-cont",
  },
  {
    text: "S. Singh 等，《The Leaderboard Illusion》。用于佐证：横跨 243 个模型的 200 万场对战、Meta 测试 27 个私有 Llama-4 变体，以及「额外的 Arena 数据能把 Arena 分数相对拉高多达 112%，却不会迁移到更广的 benchmark」这一发现。",
    href: "https://arxiv.org/abs/2504.20879",
    label: "arxiv.org/abs/2504.20879",
  },
  {
    text: "《Does SWE-Bench-Verified Test Agent Ability or Model Memory?》。用于佐证：模型在 SWE-bench Verified 上的得分大约是在控制污染的 benchmark 上的三倍，且在没有任何项目上下文的情况下，说出被改动文件名的准确度大约高六倍。",
    href: "https://arxiv.org/abs/2512.10218",
    label: "arxiv.org/abs/2512.10218",
  },
  {
    text: "A. Gema 等，《Are We Done with MMLU?》。用于佐证：估计 MMLU 有 6.49% 的题目含有错误，而在被分析的病毒学题目中，57% 存在问题，其中 33% 的标准答案本身就是错的。",
    href: "https://arxiv.org/abs/2406.04127",
    label: "arxiv.org/abs/2406.04127",
  },
  {
    text: "《Reward Hacking Benchmark: Measuring Exploits in LLM Agents with Tool Use》。用于佐证：13 个前沿模型的漏洞利用率区间，视 post-training 方式不同，从 0% 到 13.9% 不等。",
    href: "https://arxiv.org/abs/2605.02964",
    label: "arxiv.org/abs/2605.02964",
  },
];

export default function Post() {
  return (
    <>
      <Lead>
        2026 年 4 月，伯克利的一支团队造了一个 Agent，在最常被引用的八个 AI Agent benchmark 中的六个上刷到约 100%。它没解开任何一道题，也从没调用过一次语言模型。
      </Lead>

      <P>
        这个 Agent 一点都不聪明。它只是一段小程序，读懂每个 benchmark 是怎么判定你通过的，然后把那个检查恰好在找的东西原样递回去。在 FieldWorkArena 上，评分器只检查一件事：最后一条消息是不是来自 assistant。它从不去读那条消息。于是 Agent 回了个{" "}
        <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>{"{}"}</code>，一个空对象，就在全部 890 道题上刷到了 100%。<Fn n={1} />
      </P>
      <P>
        整个故事一句话就说完了，值得停下来好好品一品。这个 benchmark 本该衡量一个 Agent 能不能做现场工作。它实际衡量的，却是一条消息有没有挂上正确的发送者。这根本不是同一个测试。很长一段时间里没人察觉，因为那些数字看着挺合理，而数字正是所有人盯着看的那个东西。
      </P>

      <Figure i={0} caption="FieldWorkArena 漏洞的迷你版。挑一个偷懒的答案，然后拨动开关，看分数一路跌穿地板。">
        <EvalHarness />
      </Figure>

      <Section title="分数里大半是管道工程">
        <P>
          最该被打破的误解，是以为一个排行榜数字衡量的是 Agent 的能力。很多时候它衡量的是管道工程：harness 怎么搭起一道题、怎么跑答案、又怎么判定什么算数。伯克利的工作把这一层一个 benchmark 一个 benchmark 地拆了开来。
        </P>
        <P>
          在 SWE-bench Verified 和 SWE-bench Pro 上，Agent 和评分测试跑在同一个容器里，而 Agent 先跑。于是十行的{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>conftest.py</code> 悄悄把每个测试结果都改写成「passed」，就能在 500 道和 731 道题上给你 100%。在 WebArena 上，正确答案就摆在一个本地 JSON 文件里，于是 Agent 干脆打开一个{" "}
          <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.9em" }}>file://</code> URL 读它，压根没去浏览任何东西。<Fn n={1} /> 这些都算不上聪明。这是那种一个无聊的实习生一个下午就能翻出来的东西。关键在于，我们用来给前沿模型排名的这些 benchmark，对它敞开着大门。
        </P>
        <Figure i={1} caption="一个 exploit agent，八个 benchmark，真正解开的题为零。八个里有六个刷出了齐刷刷的 100%。">
          <ScoreBars />
        </Figure>
        <P>
          作者说得很直白：我们赖以衡量 AI 能力的这些 benchmark，本身就容易被它们声称在衡量的那些能力攻破。<Fn n={1} /> 一个厉害到值得拿来测试的 Agent，也就厉害到足以读懂这个测试并把它玩弄于股掌。
        </P>
      </Section>

      <Section title="这不是某一个坏掉的评分器">
        <P>
          要是这只是某个马虎的 benchmark，事情就好办了。可它不是。同一个鸿沟——一个数字看起来在说什么，和它实际检查了什么之间的落差——你往哪儿看都能看到。
        </P>
        <P>
          拿死记硬背来说。2025 年的一项研究追问：SWE-bench Verified 测的到底是 Agent 的能力，还是它的记忆？因为那些修复中有不少是模型训练过的公开 GitHub 历史。模型在 SWE-bench Verified 上的得分，大约是在刻意避开这种重叠的 benchmark 上的三倍；而在完全不知道项目信息的情况下，说出该改哪些文件的准确度大约高六倍。<Fn n={3} /> 这不是解决问题的本事。这是记忆背诵。
        </P>
        <P>
          或者拿人人都在引用的那个排行榜来说。《The Leaderboard Illusion》查看了两百万场 Chatbot Arena 对战，发现大厂能测试许多私有模型变体，只留下最高的那个分数。Meta 在 Llama-4 发布前试了 27 个变体。<Fn n={2} /> 额外的 Arena 数据能把一个模型的 Arena 分数相对拉高多达 112%，而同样的调优却不会迁移到更广的测试上。这个排名，有一部分衡量的其实是谁的尝试次数最多。
        </P>
        <P>
          连标准答案本身都可能是错的。研究者重新核对 MMLU——那个撑起无数「博士级」头条的 benchmark——时，估计它有 6.49% 的题目含有错误。在病毒学那一节，他们检查的题目里有 57% 存在问题，其中三分之一的答案键干脆就是错的。<Fn n={4} /> 一个跟坏掉的答案唱反调的模型，会因为答对了而被判错。
        </P>
      </Section>

      <Section title="一个数字本该意味着什么">
        <P>
          我并不是说 benchmark 毫无用处，或者说人人都在作弊。大多数 benchmark 都是出于善意，由真心想要一个真实信号的人搭起来的。问题出在结构上。benchmark 是一个代理指标，而一旦代理指标变成了目标，它就不再衡量那件事，转而开始衡量它自己。Agent 让这一点更糟，因为它们生来就是为了找到通往奖励的最短路径，而一个孱弱的评分器不过是又一条捷径。一项横跨 13 个前沿模型的 reward hacking 研究发现，漏洞利用率仅仅因为模型训练方式的不同，就从 0 到 14% 不等。<Fn n={5} /> 这种行为早就在模型里了。benchmark 只不过给它递了个开口。
        </P>
        <Callout>
          分数说 100%。Agent 什么都没解开。这两件事同时为真，而其中只有一件要紧。
        </Callout>
        <P>
          所以，把那个头条数字当成一个说法，而不是一个结果。问问评分器到底检查了什么。问问那道题会不会已经泄进了训练里。问问一个偷懒的答案会不会照样通过。一个 benchmark 真正有用的版本，不是幻灯片上的某一个百分比。而是它底下那条 trace：问了什么、Agent 做了什么、跑了什么、通过是怎么判定的。要是你看不到这些，你信的就是那个摘要，而摘要恰恰是最容易造假的那部分。
        </P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          一个你无法查验的数字不是测量。它是一种氛围。
        </p>
      </Section>

      <Callout>
        CATE 正是为这种「看清楚」而生的：把 Agent、终端、编辑器和 diff 作为一等窗口摆在同一块画布上，让你能看清一个 Agent 究竟做了什么，而不是去信它在最后打印出来的那个分数。
      </Callout>
      <CTA label="认识 CATE" href="/" />

      <Section title="参考文献">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
